import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { OtpInput } from '@/components/ui/OtpInput';
import { TextField } from '@/components/forms/TextField';
import { RecoveryCodesList } from '@/components/common/RecoveryCodesList';
import { TotpScanCard } from '@/features/auth/components/TotpScanCard';
import { useStartTotpRescan, useVerifyTotpRescan } from '@/hooks/useTotpRescan';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { OTP_UI } from '@/lib/constants/otp';
import {
  ACCOUNT_SECURITY_STRINGS as A,
  RESCAN_STRINGS as S,
  TWO_FACTOR_SETUP_STRINGS as T,
} from '@/lib/constants/two-factor-strings';
import { getApiErrorMessage, mapApiError } from '@/lib/utils/error.utils';

const FORM_ID = 'rescan-authenticator-form';
const CODE_PATTERN = new RegExp(`^\\d{${OTP_UI.CODE_LENGTH}}$`);

const START_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.CURRENT_PASSWORD_INCORRECT]: S.ERROR_PASSWORD,
  [API_ERROR_CODES.TOTP_CODE_INVALID]: T.ERROR_CODE_INVALID,
  [API_ERROR_CODES.TWO_FACTOR_LOCKED]: T.ERROR_LOCKED,
  [API_ERROR_CODES.TOTP_NOT_ENROLLED]: A.NOT_ENROLLED,
};

const VERIFY_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.TOTP_CODE_INVALID]: T.ERROR_CODE_INVALID,
  [API_ERROR_CODES.TWO_FACTOR_LOCKED]: T.ERROR_LOCKED,
  [API_ERROR_CODES.MFA_SESSION_EXPIRED]: S.ERROR_EXPIRED,
};

type Phase = 'stepUp' | 'scan' | 'codes';

interface PhaseView {
  title: string;
  description: string;
  body: ReactNode;
  footer: ReactNode;
  /** Checks the step's own fields and sends its request; returns a field error instead when one is missing. */
  submit: () => string | undefined;
}

interface RescanAuthenticatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Move the authenticator to a new app entry labelled with the current email
 * (FR-220): confirm with the password and a current code, scan the new QR
 * code and type its first code, then save the new recovery codes. Every
 * failure stays inline in this dialog, so each step can be retried.
 */
export function RescanAuthenticatorDialog({ open, onOpenChange }: RescanAuthenticatorDialogProps) {
  const start = useStartTotpRescan();
  const verify = useVerifyTotpRescan();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState<string>();

  useEffect(() => {
    if (!open) {
      setPassword('');
      setCode('');
      setFieldError(undefined);
      start.reset();
      verify.reset();
    }
    // The mutation objects change every render; resetting on open/close only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const phase: Phase = verify.isSuccess ? 'codes' : start.isSuccess ? 'scan' : 'stepUp';

  const close = () => onOpenChange(false);
  const cancel = (
    <Button variant="outline" onClick={close} disabled={start.isPending || verify.isPending}>
      {S.CANCEL}
    </Button>
  );
  const alert = (failed: { isError: boolean; error: unknown }, byCode: Record<string, string>) =>
    failed.isError && (
      <p role="alert" className="text-danger text-sm">
        {getApiErrorMessage(failed.error, mapApiError(failed.error, byCode, S.FAILED))}
      </p>
    );

  const views: Record<Phase, PhaseView> = {
    stepUp: {
      title: S.TITLE,
      description: S.STEP_UP_DESCRIPTION,
      body: (
        <>
          {alert(start, START_ERROR_BY_CODE)}
          <TextField
            label={S.PASSWORD_LABEL}
            labelVariant="caps"
            type="password"
            autoComplete="current-password"
            icon={<Lock size={16} />}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <CodeField label={S.CURRENT_CODE_LABEL} value={code} onChange={setCode} />
        </>
      ),
      submit: () => {
        if (!password) return S.PASSWORD_REQUIRED;
        if (!CODE_PATTERN.test(code)) return T.CODE_FORMAT_ERROR;
        start.mutate({ currentPassword: password, code }, { onSuccess: () => setCode('') });
        return undefined;
      },
      footer: (
        <>
          <Button type="submit" form={FORM_ID} variant="navy" disabled={start.isPending}>
            {start.isPending ? S.CONTINUING : S.CONTINUE}
          </Button>
          {cancel}
        </>
      ),
    },
    scan: {
      title: S.TITLE,
      description: S.SCAN_DESCRIPTION,
      body: (
        <>
          {alert(verify, VERIFY_ERROR_BY_CODE)}
          <TotpScanCard
            start={{ data: start.data, isPending: false, isError: false, error: null, refetch: () => undefined }}
          />
          <CodeField label={S.NEW_CODE_LABEL} value={code} onChange={setCode} />
        </>
      ),
      submit: () => {
        if (!CODE_PATTERN.test(code)) return T.CODE_FORMAT_ERROR;
        verify.mutate(code);
        return undefined;
      },
      footer: (
        <>
          <Button type="submit" form={FORM_ID} variant="navy" disabled={verify.isPending}>
            {verify.isPending ? S.VERIFYING : S.VERIFY}
          </Button>
          {cancel}
        </>
      ),
    },
    codes: {
      title: S.CODES_TITLE,
      description: S.CODES_DESCRIPTION,
      body: verify.data && <RecoveryCodesList codes={verify.data} />,
      submit: () => undefined,
      footer: (
        <Button variant="navy" className="px-6! py-2.5!" onClick={close}>
          {S.DONE}
        </Button>
      ),
    },
  };
  const view = views[phase];

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setFieldError(view.submit());
  };

  return (
    <Dialog
      open={open}
      // Esc or the overlay must not close mid-request: a step-up that lands
      // after the dialog closed would leave a re-scan nobody can finish.
      onOpenChange={(next) => (next || !(start.isPending || verify.isPending)) && onOpenChange(next)}
      title={view.title}
      description={view.description}
      size="lg"
      footer={view.footer}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {view.body}
        {fieldError && (
          <p role="alert" className="text-danger text-sm">
            {fieldError}
          </p>
        )}
      </form>
    </Dialog>
  );
}

/** The 6-digit code boxes with their label shown above them; the boxes alone label only for screen readers. */
function CodeField({ label, value, onChange }: { label: string; value: string; onChange: (code: string) => void }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <p aria-hidden="true" className="text-navy m-0 text-sm font-medium">
        {label}
      </p>
      <OtpInput label={label} value={value} onChange={onChange} length={OTP_UI.CODE_LENGTH} />
    </div>
  );
}

